import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowRight, FaArrowLeft, FaCheck, FaBalanceScale, FaTimes, FaCalendarAlt, FaUserCog, FaTrophy, FaHome } from 'react-icons/fa';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { getAccountById, updateAccount, getCrmCompanyLogoDisplayUrl } from '../../services/firestoreService';
import { getPortalEmoji } from '../../utils/portalEmojis';
import LogoLoop from '../../components/LogoLoop';
import SuccessStoriesCarousel from '../../components/start/SuccessStoriesCarousel';
import StartDashboardPreview from '../../components/start/StartDashboardPreview';
import PortalBackground from '../../components/PortalBackground';

// Department cards: tinted bg; one row with emoji + title + lead, keywords below
const DepartmentCard = ({ name, borderColor, bgClass, emoji, iconColor, keywords, lead }) => (
  <div
    className={`border-2 ${borderColor} rounded-xl overflow-hidden transition-all hover:shadow-lg h-full`}
  >
    <div
      className={`${bgClass} backdrop-blur-md py-5 px-5 md:py-6 md:px-7 flex flex-col h-full`}
    >
      <div className="flex items-center justify-center gap-4 md:gap-6">
        <div className="flex items-center gap-3">
          {emoji && (
            <span className="text-4xl md:text-5xl leading-none shrink-0" role="img" aria-hidden>
              {emoji}
            </span>
          )}
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
            {name}
          </h3>
        </div>
        {lead && (
          <>
            <div className={`w-px self-stretch shrink-0 bg-current opacity-40 ${iconColor}`} aria-hidden />
            <div className="flex items-center gap-3 shrink-0">
              <img
                src={lead.avatarUrl}
                alt={lead.name}
                className="w-16 h-16 md:w-[4.5rem] md:h-[4.5rem] rounded-full object-cover border-2 border-white/40 shadow-md"
              />
              {(() => {
                const [first, ...rest] = lead.name.split(' ');
                return (
                  <div className="text-left leading-tight">
                    <span className="block text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                      {first}
                    </span>
                    {rest.length > 0 && (
                      <span className="block text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                        {rest.join(' ')}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </>
        )}
      </div>
      <p
        className={`mt-4 md:mt-5 text-sm sm:text-base md:text-lg lg:text-xl font-semibold tracking-tight ${iconColor} flex flex-wrap justify-center items-center gap-x-2 md:gap-x-2.5 gap-y-1.5`}
      >
        {keywords.map((word, idx) => (
          <React.Fragment key={word}>
            {idx > 0 && (
              <span className="text-gray-400 dark:text-gray-500 text-base md:text-lg font-normal px-0.5 leading-none select-none" aria-hidden>
                ·
              </span>
            )}
            <span>{word}</span>
          </React.Fragment>
        ))}
      </p>
    </div>
  </div>
);

const AssessmentTemplate = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { leadId } = useParams();
  
  // State to track completed steps (1-7: Company Snapshot, BBA Team, Establish Why, Assessment, Proposed Conditions, Choose Program, Sign Up)
  const [completedSteps, setCompletedSteps] = useState(new Set());
  
  // State to control stepper step
  const [stepperStep, setStepperStep] = useState(1);
  
  // State to store company name
  const [companyName, setCompanyName] = useState(null);
  
  // State for lead data
  const [leadData, setLeadData] = useState(null);
  const [loadingLead, setLoadingLead] = useState(false);
  
  // State for Company Snapshot inputs
  const [companyYears, setCompanyYears] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [primaryProjects, setPrimaryProjects] = useState('');
  const [averageSize, setAverageSize] = useState('');
  const [projectType, setProjectType] = useState('');
  
  // State for programs
  const [programs, setPrograms] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState({});
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(true);
  const [availableItems, setAvailableItems] = useState([]);
  const [showcaseOrder, setShowcaseOrder] = useState({});
  const [selectedProgramFilter, setSelectedProgramFilter] = useState('Lite');
  const [suggestedProgram, setSuggestedProgram] = useState(null);
  const [portalInviteSent, setPortalInviteSent] = useState(false);
  const [showPortalPreview, setShowPortalPreview] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // State for success stories cards - tracks which 2 cards to show
  const [successStoriesIndex, setSuccessStoriesIndex] = useState(0);
  // State for revenue range tab selection
  const [selectedRevenueRange, setSelectedRevenueRange] = useState("0-1M");
  
  // Reset success stories index when revenue range changes
  useEffect(() => {
    setSuccessStoriesIndex(0);
  }, [selectedRevenueRange]);
  
  // Check if lead has already signed up for portal
  const hasSignedUpForPortal = leadData?.portalSignupDate ||
    leadData?.substatus === 'Signed Up For Portal' ||
    leadData?.substatus === 'Portal Signup Completed';
  const inviteSent = portalInviteSent || hasSignedUpForPortal;
  
  // Function to navigate to previous step
  const goToPreviousStep = () => {
    if (stepperStep > 1) {
      setStepperStep(stepperStep - 1);
    }
  };

  // Function to navigate to next step
  const goToNextStep = () => {
    if (stepperStep < 5) {
      setStepperStep(stepperStep + 1);
    }
  };

  // Function to find SOP IDs by title
  const findSOPIdsByTitles = async (titles) => {
    try {
      const sopTemplatesSnapshot = await getDocs(collection(db, 'sopTemplates'));
      const sopIds = [];
      
      sopTemplatesSnapshot.docs.forEach(doc => {
        const sopData = doc.data();
        if (titles.includes(sopData.title)) {
          sopIds.push(doc.id);
        }
      });
      
      return sopIds;
    } catch (error) {
      console.error('Error finding SOP IDs:', error);
      return [];
    }
  };

  // Function to send portal invite email
  const sendPortalInvite = async () => {
    if (!leadId || !leadData) {
      alert('Lead data is required to send portal invite');
      return;
    }

    // If lead has already signed up, just show the preview without sending
    if (hasSignedUpForPortal) {
      setShowPortalPreview(true);
      return;
    }

    try {
      setSendingInvite(true);

      // Get lead information
      const firstName = leadData.contact?.firstName || leadData.firstName || '';
      const lastName = leadData.contact?.lastName || leadData.lastName || '';
      const email = leadData.contact?.email || leadData.email;
      const company = leadData.company || companyName || 'your company';

      if (!email) {
        alert('Lead email is required to send portal invite');
        setSendingInvite(false);
        return;
      }

      // Find and set the free resource SOPs first
      const sopTitles = [
        'Pre-Construction Walkthrough SOP',
        'Client Intake SOP',
        'Measure Meeting SOP'
      ];
      
      const sopIds = await findSOPIdsByTitles(sopTitles);

      // Update account with free page; only set unlockedSOPs when IDs were found
      // (empty array locks every SOP on the Start portal)
      const updatedData = {
        freePage: 'operations/sops'
      };
      if (sopIds.length > 0) {
        updatedData.unlockedSOPs = sopIds;
      }
      
      await updateAccount(leadId, updatedData);
      // Keep local lead data in step so the preview modal reflects the free page we just granted
      setLeadData((prev) => (prev ? { ...prev, ...updatedData } : prev));

      // Create portal invite and send custom email using the combined Firebase function
      const functionUrl = `https://us-central1-bba-portal-894c2.cloudfunctions.net/createAndSendPortalInvite`;
      // The function only sends invites for signed-in internal users.
      const idToken = await auth.currentUser?.getIdToken();
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify({
          leadId,
          email,
          firstName,
          lastName,
          company,
          // No body: the function renders the branded invite template (functions/portalInviteEmail.js)
          subject: 'Next Steps with BBA - Portal Invite'
        })
      });

      const inviteResult = await response.json();

      if (!inviteResult.success) {
        throw new Error(inviteResult.error || 'Failed to create and send portal invite');
      }

      setPortalInviteSent(true);
      setShowPortalPreview(true);
    } catch (error) {
      console.error('Error sending portal invite:', error);
      alert(`Failed to send portal invite: ${error.message}`);
    } finally {
      setSendingInvite(false);
    }
  };
  
  // Load lead data if leadId is provided
  useEffect(() => {
    const loadLeadData = async () => {
      if (leadId) {
        try {
          setLoadingLead(true);
          const lead = await getAccountById(leadId);
          if (lead) {
            setLeadData(lead);
            // Set company name from lead data
            const leadCompanyName = lead.companyName || lead.company;
            if (leadCompanyName) {
              setCompanyName(leadCompanyName);
            }
          }
        } catch (error) {
          console.error('Error loading lead data:', error);
        } finally {
          setLoadingLead(false);
        }
      }
    };

    loadLeadData();
  }, [leadId]);

  // Load company name from Firebase (optional, for display only)
  useEffect(() => {
    const loadCompanyName = async () => {
      // Skip if we already have company name from lead data
      if (companyName && leadData) return;
      
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Load company name from user data first
            if (userData.company) {
              setCompanyName(userData.company);
            }
            
            // Try to get company name from account
            let accountId = userData.accountId;
            
            // If no accountId, try to find account by email
            if (!accountId && currentUser.email) {
              try {
                const normalizedEmail = currentUser.email.toLowerCase().trim();
                
                // Try multiple strategies to find the account
                const searchStrategies = [
                  query(
                    collection(db, 'accounts'),
                    where('contact.email', '==', normalizedEmail),
                    where('type', '==', 'Leads')
                  ),
                  query(
                    collection(db, 'accounts'),
                    where('email', '==', normalizedEmail),
                    where('type', '==', 'Leads')
                  ),
                  query(
                    collection(db, 'accounts'),
                    where('contact.email', '==', normalizedEmail)
                  ),
                  query(
                    collection(db, 'accounts'),
                    where('email', '==', normalizedEmail)
                  )
                ];
                
                for (const searchQuery of searchStrategies) {
                  try {
                    const accountsSnapshot = await getDocs(searchQuery);
                    if (!accountsSnapshot.empty) {
                      accountId = accountsSnapshot.docs[0].id;
                      break;
                    }
                  } catch (queryError) {
                    console.warn('Query strategy failed:', queryError);
                  }
                }
              } catch (error) {
                console.error('Error finding account by email:', error);
              }
            }
            
            // Load company name from account if we have accountId
            if (accountId && !companyName) {
              try {
                const accountDoc = await getDoc(doc(db, 'accounts', accountId));
                if (accountDoc.exists()) {
                  const accountData = accountDoc.data();
                  if (accountData.company) {
                    setCompanyName(accountData.company);
                  }
                }
              } catch (error) {
                console.error('Error loading account data:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error loading company name:', error);
        }
      }
    };

    loadCompanyName();
  }, [currentUser, companyName, leadData]);

  // Load programs and billing cycles
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        setIsLoadingPrograms(true);
        
        // Load programs data
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        if (!programsSnapshot.empty) {
          const programsData = {};
          programsSnapshot.forEach(doc => {
            const programData = doc.data();
            if (programData && typeof programData === 'object') {
              if (!programData.features || typeof programData.features !== 'object') {
                programData.features = {};
              }
              if (!programData.name) programData.name = doc.id;
              if (!programData.description) programData.description = 'Program description';
              if (!programData.pricePerMonth) programData.pricePerMonth = 0;
              if (typeof programData.isMostPopular !== 'boolean') programData.isMostPopular = false;
              
              programsData[doc.id] = programData;
            }
          });
          setPrograms(programsData);
        }

        // Load billing cycles data
        const billingSnapshot = await getDocs(collection(db, 'billingCycles'));
        if (!billingSnapshot.empty) {
          const billingData = {};
          billingSnapshot.forEach(doc => {
            billingData[doc.id] = doc.data();
          });
          setBillingCycles(billingData);
          
          // Set first billing cycle as default
          if (Object.keys(billingData).length > 0) {
            setBillingCycle(Object.keys(billingData)[0]);
          }
        }

        // Load available items from alaCarteItems collection
        const itemsSnapshot = await getDocs(collection(db, 'alaCarteItems'));
        const itemsData = itemsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAvailableItems(itemsData);

        // Load showcase order from settings collection
        const settingsSnapshot = await getDocs(collection(db, 'settings'));
        const showcaseOrderDoc = settingsSnapshot.docs.find(doc => doc.id === 'showcaseOrder');
        if (showcaseOrderDoc) {
          setShowcaseOrder(showcaseOrderDoc.data().order || {});
        }
      } catch (error) {
        console.error('Error loading programs:', error);
      } finally {
        setIsLoadingPrograms(false);
      }
    };

    loadPrograms();
  }, []);

  // Helper functions for pricing
  const getAnnualPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * billingCycles.annual.months * (1 - billingCycles.annual.discount / 100));
    } else if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth * 12;
  };

  const getMonthlyPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * (1 - billingCycles.annual.discount / 100));
    } else if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth;
  };

  const getQuarterlyPrice = (pricePerMonth) => {
    if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth * 3;
  };

  // Same order as Program Templates: others first (object order), then Pro, Premier, BBA Bundle
  const getOrderedPrograms = () => {
    if (!programs || Object.keys(programs).length === 0) return [];
    const tailOrder = ['Pro', 'Premier', 'BBA Bundle'];
    const orderIndex = (key) => {
      const i = tailOrder.indexOf(key);
      return i >= 0 ? i + 1000 : 0;
    };
    return Object.entries(programs).sort(([keyA], [keyB]) => orderIndex(keyA) - orderIndex(keyB));
  };

  // Validate program data
  const validateProgram = (program) => {
    if (!program || typeof program !== 'object') {
      return {
        name: 'Unknown Program',
        description: 'Program description',
        pricePerMonth: 0,
        isMostPopular: false,
        minimumTerm: 12,
        features: {}
      };
    }
    
    return {
      name: program.name || 'Unknown Program',
      description: program.description || 'Program description',
      pricePerMonth: program.pricePerMonth || 0,
      isMostPopular: program.isMostPopular || false,
      minimumTerm: program.minimumTerm || 12,
      features: program.features && typeof program.features === 'object' ? program.features : {}
    };
  };

  // Get ordered showcase features
  const getOrderedShowcaseFeatures = (programKey) => {
    const program = programs[programKey];
    if (!program || !program.features) return [];
    
    const showcaseFeatures = Object.entries(program.features)
      .filter(([_, featureData]) => featureData.showcase)
      .map(([featureName, _]) => featureName);
    
    const savedOrder = showcaseOrder[programKey] || [];
    
    if (savedOrder.length === 0 && showcaseFeatures.length > 0) {
      return showcaseFeatures;
    }
    
    // Sort features according to saved order
    const orderedFeatures = [];
    savedOrder.forEach(featureName => {
      if (showcaseFeatures.includes(featureName)) {
        orderedFeatures.push(featureName);
      }
    });
    
    // Add any features not in saved order
    showcaseFeatures.forEach(featureName => {
      if (!orderedFeatures.includes(featureName)) {
        orderedFeatures.push(featureName);
      }
    });
    
    return orderedFeatures;
  };

  // Calendar data for each program
  const getCalendarEvents = (programKey) => {
    const calendars = {
      'Lite': [
        { day: 2, event: 'Financial Review', color: 'darkgreen' },
        { day: 3, event: 'Breakthrough Call', color: 'green' },
        { day: 5, event: 'Breakthrough Call', color: 'green' },
        { day: 10, event: 'Breakthrough Call', color: 'green' },
        { day: 11, event: 'Strategy Call', color: 'red' },
        { day: 12, event: 'Breakthrough Call', color: 'green' },
        { day: 16, event: 'Strategy Call', color: 'red' },
        { day: 17, event: 'Breakthrough Call', color: 'green' },
        { day: 19, event: 'Breakthrough Call', color: 'green' },
        { day: 24, event: 'Breakthrough Call', color: 'green' },
        { day: 26, event: 'Breakthrough Call', color: 'green' },
      ],
      'Plus': [
        { day: 2, event: 'Financial Review', color: 'darkgreen' },
        { day: 3, event: 'Breakthrough Call', color: 'green' },
        { day: 5, event: 'Breakthrough Call', color: 'green' },
        { day: 6, event: 'Strategy Call', color: 'red' },
        { day: 9, event: 'Strategy Call', color: 'red' },
        { day: 10, event: 'Breakthrough Call', color: 'green' },
        { day: 12, event: 'Breakthrough Call', color: 'green' },
        { day: 17, event: 'Breakthrough Call', color: 'green' },
        { day: 19, event: 'Breakthrough Call', color: 'green' },
        { day: 20, event: 'Strategy Call', color: 'red' },
        { day: 23, event: 'Strategy Call', color: 'red' },
        { day: 24, event: 'Breakthrough Call', color: 'green' },
        { day: 26, event: 'Breakthrough Call', color: 'green' },
      ],
      'Pro': [
        { day: 2, event: 'Financial Review', color: 'darkgreen' },
        { day: 3, event: 'Breakthrough Call', color: 'green' },
        { day: 4, event: 'Strategy Call', color: 'red' },
        { day: 5, event: 'Breakthrough Call', color: 'green' },
        { day: 6, event: 'Strategy Call', color: 'red' },
        { day: 9, event: 'Strategy Call', color: 'red' },
        { day: 10, event: 'Breakthrough Call', color: 'green' },
        { day: 11, event: 'Strategy Call', color: 'red' },
        { day: 12, event: 'CQC Visit', color: 'gold' },
        { day: 12, event: 'Breakthrough Call', color: 'green' },
        { day: 13, event: 'CQC Visit', color: 'gold' },
        { day: 16, event: 'Strategy Call', color: 'red' },
        { day: 17, event: 'Breakthrough Call', color: 'green' },
        { day: 19, event: 'Breakthrough Call', color: 'green' },
        { day: 20, event: 'Strategy Call', color: 'red' },
        { day: 23, event: 'Strategy Call', color: 'red' },
        { day: 24, event: 'Breakthrough Call', color: 'green' },
        { day: 26, event: 'Breakthrough Call', color: 'green' },
        { day: 27, event: 'Strategy Call', color: 'red' },
      ],
      'Bundle': [
        { day: 2, event: 'Financial Review', color: 'darkgreen' },
        { day: 2, event: 'Strategy Call', color: 'red' }, // Monday
        { day: 3, event: 'Breakthrough Call', color: 'green' },
        { day: 4, event: 'Strategy Call', color: 'red' }, // Wednesday
        { day: 5, event: 'Breakthrough Call', color: 'green' },
        { day: 6, event: 'Strategy Call', color: 'red' }, // Friday
        { day: 9, event: 'Strategy Call', color: 'red' }, // Monday
        { day: 10, event: 'Breakthrough Call', color: 'green' },
        { day: 11, event: 'Strategy Call', color: 'red' }, // Wednesday
        { day: 12, event: 'Breakthrough Call', color: 'green' },
        { day: 13, event: 'Strategy Call', color: 'red' }, // Friday
        { day: 16, event: 'Strategy Call', color: 'red' }, // Monday
        { day: 17, event: 'Breakthrough Call', color: 'green' },
        { day: 18, event: 'Strategy Call', color: 'red' }, // Wednesday
        { day: 19, event: 'On-Site Day 1', color: 'gold' },
        { day: 19, event: 'Breakthrough Call', color: 'green' },
        { day: 20, event: 'On-Site Day 2', color: 'gold' },
        { day: 20, event: 'Strategy Call', color: 'red' }, // Friday
        { day: 23, event: 'Strategy Call', color: 'red' }, // Monday
        { day: 24, event: 'Breakthrough Call', color: 'green' },
        { day: 25, event: 'Strategy Call', color: 'red' }, // Wednesday
        { day: 26, event: 'Breakthrough Call', color: 'green' },
        { day: 27, event: 'Strategy Call', color: 'red' }, // Friday
      ],
    };

    return calendars[programKey] || [];
  };

  // Get all events for "All" filter
  const getAllCalendarEvents = () => {
    const allEvents = [];
    ['Lite', 'Plus', 'Pro', 'Bundle'].forEach(programKey => {
      const events = getCalendarEvents(programKey);
      events.forEach(event => {
        if (!allEvents.find(e => e.day === event.day && e.event === event.event)) {
          allEvents.push({ ...event, program: programKey });
        }
      });
    });
    return allEvents.sort((a, b) => a.day - b.day);
  };

  // Get events for selected filter
  const getFilteredEvents = () => {
    if (selectedProgramFilter === 'All') {
      return getAllCalendarEvents();
    }
    
    // Map filter names to program keys
    const filterMap = {
      'Lite': 'Lite',
      'Plus': 'Plus',
      'Pro': 'Pro',
      'Bundle': 'Bundle'
    };
    
    const programKey = filterMap[selectedProgramFilter];
    return programKey ? getCalendarEvents(programKey) : [];
  };

  // Get color class for event - always use event-specific colors
  const getEventColorClass = (eventColor) => {
    const colorMap = {
      'green': 'bg-green-600',
      'red': 'bg-red-600',
      'purple': 'bg-purple-600',
      'orange': 'bg-orange-600',
      'blue': 'bg-blue-600',
      'gold': 'bg-yellow-400',
      'darkgreen': 'bg-green-800',
    };
    return colorMap[eventColor] || 'bg-gray-600';
  };

  // Render calendar
  const renderCalendar = () => {
    const events = getFilteredEvents();
    const daysInMonth = 28; // February 2026
    const firstDayOfWeek = 0; // February 1, 2026 is a Sunday
    
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Add empty cells for days before the 1st
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = events.filter(e => e.day === day);
      days.push({ day, events: dayEvents });
    }
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center mb-4">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">February 2026</h3>
        </div>

        
        {/* Day names */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-sm font-semibold text-gray-700 dark:text-gray-300 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((dayData, index) => {
            if (dayData === null) {
              return <div key={`empty-${index}`} className="aspect-square"></div>;
            }
            
            const { day, events: dayEvents } = dayData;
            
            return (
              <div
                key={day}
                className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex flex-col items-center justify-start border border-gray-200 dark:border-gray-600"
              >
                <div className="text-sm font-medium text-white mb-1">{day}</div>
                <div className="w-full space-y-1 flex-1 overflow-y-auto">
                  {dayEvents.map((event, eventIndex) => (
                    <div
                      key={`${day}-${eventIndex}`}
                      className={`${getEventColorClass(event.color)} text-white text-xs px-1 py-0.5 rounded truncate`}
                      title={event.event}
                    >
                      {event.event}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-4 md:p-6 relative">
      <PortalBackground />
      <div className="relative w-full max-w-full">
        {/* Titles re-animate on step change via the keyed wrapper */}
        <div key={`step-title-${stepperStep}`} className="tab-fade-in">
        {/* Title and Subtitle - Show for Step 1 */}
        {stepperStep === 1 && (
          <div className="text-center mb-8">
            {/* Company Logo */}
            {getCrmCompanyLogoDisplayUrl(leadData) && (
              <div className="flex justify-center mb-6">
                <div className="max-w-xs rounded-2xl border border-gray-200 bg-white p-3 shadow-lg">
                  <img
                    src={getCrmCompanyLogoDisplayUrl(leadData)}
                    alt={`${companyName || leadData?.company || 'Company'} logo`}
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            )}
            <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
              {companyName ? `The ${companyName} Story` : 'Your Story'}
            </h3>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Tell us about yourself and your company.
            </p>
          </div>
        )}

        {/* Title and Subtitle - Show for Step 2 */}
        {stepperStep === 2 && (
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src="/bba-emblem.svg" 
                alt="BBA Emblem" 
                className="h-24 w-auto"
              />
            </div>
            <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
              The Builders Breakthrough Story
            </h3>
          </div>
        )}

        {/* Title and Subtitle - Show for Step 3 */}
        {stepperStep === 3 && (
          <div className="text-center mb-8">
            {/* Company Logo */}
            {getCrmCompanyLogoDisplayUrl(leadData) && (
              <div className="flex justify-center mb-6">
                <div className="max-w-xs rounded-2xl border border-gray-200 bg-white p-3 shadow-lg">
                  <img
                    src={getCrmCompanyLogoDisplayUrl(leadData)}
                    alt={`${companyName || leadData?.company || 'Company'} logo`}
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            )}
            <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
              Establish Your Objective
            </h3>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Define your objective and goals.
            </p>
          </div>
        )}

        {/* Title and Subtitle - Show for Step 4 */}
        {stepperStep === 4 && (
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src="/bba-emblem.svg" 
                alt="BBA Emblem" 
                className="h-24 w-auto"
              />
            </div>
            <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
              Success Stories
            </h3>
          </div>
        )}

        {/* Title - Show for Step 5 */}
        {stepperStep === 5 && (
          <div className="text-center mb-8">
            {/* Company Logo */}
            {getCrmCompanyLogoDisplayUrl(leadData) && (
              <div className="flex justify-center mb-6">
                <div className="max-w-xs rounded-2xl border border-gray-200 bg-white p-3 shadow-lg">
                  <img
                    src={getCrmCompanyLogoDisplayUrl(leadData)}
                    alt={`${companyName || leadData?.company || 'Company'} logo`}
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            )}
            <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
              Next Steps
            </h3>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Here's what happens next in your journey with us.
            </p>
          </div>
        )}
        </div>

        {/* Step Indicators */}
        <div className="flex justify-center items-center gap-4 mb-8">
          {/* Left Arrow */}
          <button
            onClick={goToPreviousStep}
            disabled={stepperStep === 1}
            className={`flex h-10 w-10 items-center justify-center rounded-full font-semibold transition-colors ${
              stepperStep === 1
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-50'
                : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-500 cursor-pointer'
            }`}
            aria-label="Previous step"
          >
            <FaArrowLeft className="h-5 w-5" />
          </button>

          {[1, 2, 3, 4, 5].map((stepNum) => {
            return (
              <React.Fragment key={stepNum}>
                <div
                  onClick={() => setStepperStep(stepNum)}
                  className="relative cursor-pointer outline-none focus:outline-none"
                >
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full font-semibold transition-colors ${
                      stepperStep === stepNum
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-400 text-white hover:bg-gray-500'
                    }`}
                  >
                    <span className="text-sm text-white">{stepNum}</span>
                  </div>
                </div>
                {stepNum < 5 && (
                  <div className="h-1 w-12 bg-gray-400" />
                )}
              </React.Fragment>
            );
          })}

          {/* Right Arrow */}
          <button
            onClick={goToNextStep}
            disabled={stepperStep === 5}
            className={`flex h-10 w-10 items-center justify-center rounded-full font-semibold transition-colors ${
              stepperStep === 5
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-50'
                : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-500 cursor-pointer'
            }`}
            aria-label="Next step"
          >
            <FaArrowRight className="h-5 w-5" />
          </button>
        </div>

        {/* Step Content */}
        <div key={`step-content-${stepperStep}`} className="w-full min-h-[500px] tab-fade-in">
          {/* Step 1: Background */}
          {stepperStep === 1 && (
            <div className="pt-[17px] pb-8">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Card 1: Your Background */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <span className="text-6xl leading-none" role="img" aria-label="Your background">👤</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Your Background
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Tell us about yourself.
                    </p>
                  </div>

                  {/* Card 2: Company History */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <span className="text-6xl leading-none" role="img" aria-label="Company history">📜</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Company History
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Your company and its history.
                    </p>
                  </div>

                  {/* Card 3: Revenue Trend */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <span className="text-6xl leading-none" role="img" aria-label="Revenue trend">📈</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Revenue Trend
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Describe your revenue patterns.
                    </p>
                  </div>

                  {/* Card 4: Team Size */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <span className="text-6xl leading-none" role="img" aria-label="Team size">👥</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Team Size
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Your team structure and size.
                    </p>
                  </div>

                  {/* Card 5: Market */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <span className="text-6xl leading-none" role="img" aria-label="Market">🌎</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Market
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Your target market and position.
                    </p>
                  </div>

                  {/* Card 6: Projects Focus */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <span className="text-6xl leading-none" role="img" aria-label="Projects focus">🏠</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Projects Focus
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Your primary projects and focus.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Learn about the BBA Team & Process */}
          {stepperStep === 2 && (
            <div className="py-8 w-full">
              {/* Proven Systems Section */}
              <div className="mb-12 flex justify-center items-center gap-8">
                {/* Proven Systems */}
                <div className="flex flex-col items-center">
                  <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Proven Systems
                  </h4>
                  <img 
                    src="/cqclogo.png" 
                    alt="CQC Logo" 
                    className="h-48 w-auto"
                  />
                </div>

                {/* Arrow */}
                <FaArrowRight className="text-4xl text-white" />

                {/* Proof of Concept */}
                <div className="flex flex-col items-center">
                  <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Proof of Concept
                  </h4>
                  <img 
                    src="/big6.png" 
                    alt="Big 6" 
                    className="h-48 w-auto"
                  />
                </div>

                {/* Arrow */}
                <FaArrowRight className="text-4xl text-white" />

                {/* Fully Deployed */}
                <div className="flex flex-col items-center">
                  <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Fully Deployed
                  </h4>
                  <img 
                    src="/deploy.png" 
                    alt="Deploy" 
                    className="h-48 w-auto"
                  />
                </div>
              </div>

              {/* Departments + leads */}
              <div className="mb-8 flex justify-center">
                <div className="w-full max-w-[1800px] px-4 md:px-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
                    <DepartmentCard
                      name="SALES"
                      borderColor="border-red-500/35"
                      bgClass="bg-red-500/15 dark:bg-red-950/50"
                      emoji={getPortalEmoji('sales')}
                      iconColor="text-red-600 dark:text-red-400"
                      keywords={['Model', 'Process', 'Strategy']}
                      lead={{ name: 'Ken Combs', avatarUrl: '/kenshot.png' }}
                    />
                    <DepartmentCard
                      name="OPERATIONS"
                      borderColor="border-blue-500/35"
                      bgClass="bg-blue-500/15 dark:bg-blue-950/50"
                      emoji={getPortalEmoji('operations')}
                      iconColor="text-blue-600 dark:text-blue-400"
                      keywords={['Standardize', 'Systemize', 'Incentivize']}
                      lead={{ name: 'Daryl Mejeur', avatarUrl: '/darylshot.png' }}
                    />
                    <DepartmentCard
                      name="FINANCE"
                      borderColor="border-green-500/35"
                      bgClass="bg-green-500/15 dark:bg-green-950/50"
                      emoji={getPortalEmoji('finance')}
                      iconColor="text-green-600 dark:text-green-400"
                      keywords={['Analysis', 'Clarity', 'Forecast']}
                      lead={{ name: 'Amanda Mitchell', avatarUrl: '/amandashot.png' }}
                    />
                    <DepartmentCard
                      name="TECH & AI"
                      borderColor="border-purple-500/35"
                      bgClass="bg-purple-500/15 dark:bg-purple-950/50"
                      emoji={getPortalEmoji('technology')}
                      iconColor="text-purple-600 dark:text-purple-400"
                      keywords={['Optimize', 'Integrate', 'Automate']}
                      lead={{ name: 'Ian Riddle', avatarUrl: '/riddleshot.png' }}
                    />
                    <DepartmentCard
                      name="MARKETING"
                      borderColor="border-orange-500/35"
                      bgClass="bg-orange-500/15 dark:bg-orange-950/50"
                      emoji={getPortalEmoji('marketing')}
                      iconColor="text-orange-600 dark:text-orange-400"
                      keywords={['Lead-Gen', 'Qualify', 'Analyze']}
                      lead={{ name: 'Parker Sexton', avatarUrl: '/parker.png' }}
                    />
                    <DepartmentCard
                      name="HR"
                      borderColor="border-amber-500/40"
                      bgClass="bg-amber-400/20 dark:bg-amber-950/50"
                      emoji={getPortalEmoji('hr')}
                      iconColor="text-amber-700 dark:text-amber-400"
                      keywords={['Hiring', 'Firing', 'Compliance']}
                      lead={{ name: 'Annette Rushin', avatarUrl: '/annshot.png' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Establish Your Objective */}
          {stepperStep === 3 && (
            <div className="py-8">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Card 1: Your Objective */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <span className="text-6xl leading-none" role="img" aria-label="Primary objective">🎯</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Primary Objective
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      What is your primary goal? What do you want to achieve?
                    </p>
                  </div>

                  {/* Card 2: Short-Term Goals */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <span className="text-6xl leading-none" role="img" aria-label="Short-term goals">✅</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Short-Term Goals
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      What do you want to accomplish in the next 3-6 months?
                    </p>
                  </div>

                  {/* Card 3: Long-Term Goals */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <span className="text-6xl leading-none" role="img" aria-label="Long-term goals">🔭</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Long-Term Goals
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Where do you want to be in 1-3 years?
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Success Stories */}
          {stepperStep === 4 && (
            <div className="pt-[7px] pb-8">
              <div className="max-w-6xl mx-auto">
                {/* Success Stories Cards with Navigation */}
                <SuccessStoriesCarousel
                  theme="crm"
                  successStoriesIndex={successStoriesIndex}
                  setSuccessStoriesIndex={setSuccessStoriesIndex}
                  selectedRevenueRange={selectedRevenueRange}
                  setSelectedRevenueRange={setSelectedRevenueRange}
                />
                {/* Logo Loop */}
                <div className="bg-white rounded-lg flex items-center mb-12" style={{ height: '160px', position: 'relative', overflow: 'hidden' }}>
                  <LogoLoop
                    logos={[
                      { src: "/eastman.png", alt: "Eastman" },
                      { src: "/h2h.png", alt: "H2H" },
                      { src: "/cann.png", alt: "Cann" },
                      { src: "/hhi.png", alt: "HHI" },
                      { src: "/idb.png", alt: "IDB" },
                      { src: "/art.png", alt: "Art" },
                      { src: "/gh.png", alt: "GH" },
                      { src: "/slate.png", alt: "Slate" },
                      { src: "/coho.png", alt: "Coho" },
                      { src: "/hut.png", alt: "Hut" },
                      { src: "/hum.png", alt: "Hum" },
                      { src: "/heart.png", alt: "Heart" },
                      { src: "/mur.png", alt: "Mur" },
                      { src: "/spring.png", alt: "Spring" }
                    ]}
                    speed={40}
                    direction="left"
                    logoHeight={100}
                    gap={80}
                    hoverSpeed={0}
                    scaleOnHover
                    fadeOut
                    fadeOutColor="#ffffff"
                    ariaLabel="Partner logos"
                  />
                </div>

                {/* Partner Showcase Gallery */}
                <div className="mb-12">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/cannpic.png" 
                        alt="Cann Construction" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/hhipic.png" 
                        alt="HHI Builders" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/idbpic.png" 
                        alt="IDB" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/eastpic.png" 
                        alt="Eastman" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/artpic.png" 
                        alt="Artisan" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/craftpic.png" 
                        alt="Craft Design + Build" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/h2hpic.png" 
                        alt="H2H" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/slatepic.png" 
                        alt="Slate" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/cohpic.png" 
                        alt="Coho" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/tablerpic.png" 
                        alt="Tabler" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/seepic.png" 
                        alt="See" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img 
                        src="/panpic.png" 
                        alt="Pan" 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>
                </div>

                {/* YouTube Videos Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Artisan Remodeling Video */}
                  <div className="text-center">
                    <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                      SEE WHAT ARTISAN REMODELING HAD TO SAY
                    </h3>
                    <div className="flex justify-center">
                      <iframe 
                        width="100%" 
                        height="389" 
                        src="https://www.youtube.com/embed/3xaIQxT0qRg" 
                        title="Onsite with Artisan Remodeling in Fort Collins, Colorado | Builders Breakthrough Advisors" 
                        frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                        referrerPolicy="strict-origin-when-cross-origin" 
                        allowFullScreen
                        className="rounded-lg w-full aspect-video"
                      ></iframe>
                    </div>
                  </div>

                  {/* Craft Design + Build Video */}
                  <div className="text-center">
                    <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                      SEE WHAT CRAFT DESIGN + BUILD HAD TO SAY
                    </h3>
                    <div className="flex justify-center">
                      <iframe 
                        width="100%" 
                        height="389" 
                        src="https://www.youtube.com/embed/2rqhILIPCGQ" 
                        title="BBA : Helping Business Scale to $10M +" 
                        frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                        referrerPolicy="strict-origin-when-cross-origin" 
                        allowFullScreen
                        className="rounded-lg w-full aspect-video"
                      ></iframe>
                    </div>
                  </div>

                  {/* Cann Construction Video */}
                  <div className="text-center">
                    <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                      SEE WHAT CANN CONSTRUCTION HAD TO SAY
                    </h3>
                    <div className="flex justify-center">
                      <iframe 
                        width="100%" 
                        height="389" 
                        src="https://www.youtube.com/embed/ELWzX7X3sNw" 
                        title="Another BBA Success : &quot;More Than I Could Have Asked For...&quot;" 
                        frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                        referrerPolicy="strict-origin-when-cross-origin" 
                        allowFullScreen
                        className="rounded-lg w-full aspect-video"
                      ></iframe>
                    </div>
                  </div>

                  {/* HHI Builders Video */}
                  <div className="text-center">
                    <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                      SEE WHAT HHI BUILDERS HAD TO SAY
                    </h3>
                    <div className="flex justify-center">
                      <iframe 
                        width="100%" 
                        height="389" 
                        src="https://www.youtube.com/embed/4vZNSch5ENc" 
                        title="Don&#39;t Just Take Our Word For It - HHI Builders" 
                        frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                        referrerPolicy="strict-origin-when-cross-origin" 
                        allowFullScreen
                        className="rounded-lg w-full aspect-video"
                      ></iframe>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Next Steps */}
          {stepperStep === 5 && (
            <div className="py-8">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {/* Portal Invite */}
                  <div
                    className={`backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border relative transition-all duration-500 ${
                      inviteSent
                        ? 'bg-green-500/20 dark:bg-green-500/15 border-green-500/50 dark:border-green-400/40'
                        : 'bg-white/20 dark:bg-white/10 border-white/30 dark:border-white/20'
                    }`}
                    style={inviteSent ? { animation: 'inviteGlow 1.2s ease-out' } : undefined}
                  >
                    <style>{`
                      @keyframes invitePop { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.3); opacity: 1; } 100% { transform: scale(1); } }
                      @keyframes inviteGlow { 0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); } 100% { box-shadow: 0 0 0 16px rgba(34,197,94,0); } }
                    `}</style>
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div
                        className={`w-10 h-10 rounded-full text-white flex items-center justify-center text-xl font-bold transition-colors duration-500 ${
                          inviteSent ? 'bg-green-600' : 'bg-red-600'
                        }`}
                      >
                        {inviteSent ? (
                          <FaCheck className="text-lg" style={{ animation: 'invitePop 0.5s ease-out' }} />
                        ) : (
                          '1'
                        )}
                      </div>
                    </div>
                    <div className="mb-6 flex justify-center mt-4">
                      <span className="text-6xl leading-none" role="img" aria-label="Portal invite">✉️</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Portal Invite
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      You'll receive an invitation to access your personalized portal where you can track progress and access resources.
                    </p>
                    <button
                      onClick={sendPortalInvite}
                      disabled={sendingInvite || portalInviteSent}
                      className={`mt-6 w-full px-6 py-3 rounded-lg text-base font-semibold transition-colors ${
                        hasSignedUpForPortal
                          ? 'bg-green-800 hover:bg-green-900 text-white'
                          : portalInviteSent
                          ? 'bg-green-600 text-white cursor-default'
                          : sendingInvite
                          ? 'bg-gray-400 cursor-not-allowed text-white'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                    >
                      {sendingInvite
                        ? 'Sending...'
                        : hasSignedUpForPortal
                        ? 'Signup Completed'
                        : portalInviteSent
                        ? 'Invite Sent'
                        : 'Send Portal Invite'}
                    </button>
                    {portalInviteSent && !hasSignedUpForPortal && (
                      <p className="mt-3 text-xs text-gray-600 dark:text-gray-400 text-center">
                        💡 <strong>Tip:</strong> Check your spam and promotions folder.
                      </p>
                    )}
                  </div>

                  {/* Assessment */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20 relative">
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center text-xl font-bold">
                        2
                      </div>
                    </div>
                    <div className="mb-6 flex justify-center mt-4">
                      <span className="text-6xl leading-none" role="img" aria-label="Assessment">📋</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Assessment
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Complete your business assessment so we can build your analysis and recommended implementations.
                    </p>
                  </div>

                  {/* Analysis Call */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20 relative">
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center text-xl font-bold">
                        3
                      </div>
                    </div>
                    <div className="mb-6 flex justify-center mt-4">
                      <span className="text-6xl leading-none" role="img" aria-label="Analysis call">📞</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Analysis Call
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Schedule a call with our team to discuss your goals, answer questions, and plan your path forward.
                    </p>
                  </div>

                  {/* Get Started */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col shadow-lg border border-white/30 dark:border-white/20 relative">
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center text-xl font-bold">
                        4
                      </div>
                    </div>
                    <div className="mb-6 flex justify-center mt-4">
                      <span className="text-6xl leading-none" role="img" aria-label="Get started">🚀</span>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                      Get Started
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-center">
                      Begin your journey with BBA and start implementing strategies to achieve your business objectives.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Portal Preview Modal */}
      {showPortalPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          {/*
            The transform makes this box the containing block for PortalBackground's
            `position: fixed` layer, so the same ambient BBA background the lead sees on
            their Start Dashboard fills the modal instead of the whole viewport. The inner
            div is the scroller, so the background stays put behind the content like the real page.
          */}
          <div
            className="rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden relative my-8"
            style={{ transform: 'translateZ(0)' }}
          >
            <PortalBackground />

            {/* Close Button */}
            <button
              onClick={() => setShowPortalPreview(false)}
              className="absolute top-4 right-4 z-20 p-2 rounded-lg bg-white/70 dark:bg-gray-700/70 backdrop-blur hover:bg-white dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors"
            >
              <FaTimes className="w-5 h-5" />
            </button>

            <div className={`relative max-h-[95vh] overflow-y-auto ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <StartDashboardPreview
                companyName={companyName || leadData?.company || 'Your Company'}
                freePage={leadData?.freePage || null}
                firstName={
                  leadData?.contact?.firstName ||
                  leadData?.firstName ||
                  companyName?.split(/\s+/)[0] ||
                  'there'
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentTemplate;

